import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

export interface AiBundleItem { product_id: string; qty: number }
export interface AiBundleRow {
  id: string;
  objective: string;
  title: string;
  description: string | null;
  items: AiBundleItem[];
  discount_pct: number | null;
}

const OBJECTIVE_META: Record<string, { es: string; en: string; icon: string }> = {
  pre_workout:     { es: "Pre-entreno", en: "Pre-workout", icon: "⚡" },
  post_workout:    { es: "Post-entreno", en: "Post-workout", icon: "💪" },
  muscle_gain:     { es: "Ganar músculo", en: "Muscle gain", icon: "🏋️" },
  fat_loss:        { es: "Quemar grasa", en: "Fat loss", icon: "🔥" },
  energy_focus:    { es: "Energía y enfoque", en: "Energy & focus", icon: "✨" },
  recovery_sleep:  { es: "Recuperación", en: "Recovery", icon: "🌙" },
};

export function objectiveLabel(objective: string, lang: "es" | "en") {
  const m = OBJECTIVE_META[objective];
  if (!m) return objective;
  return `${m.icon} ${lang === "es" ? m.es : m.en}`;
}

/**
 * Reads active globally-generated AI bundles (cron source). RLS exposes
 * rows where `is_active=true AND source='cron'` to anyone.
 */
export function useAiBundles(limit = 6) {
  const [bundles, setBundles] = useState<AiBundleRow[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const fetchBundles = async () => {
      setLoading(true);
      const { data, error } = await supabase
        .from("ai_bundles")
        .select("id, objective, title, description, items, discount_pct")
        .eq("is_active", true)
        .eq("source", "cron")
        .order("created_at", { ascending: false })
        .limit(limit);
      if (cancelled) return;
      setLoading(false);
      if (error || !Array.isArray(data)) { setBundles([]); return; }
      setBundles(
        data.map((r: any) => ({
          id: String(r.id),
          objective: String(r.objective ?? ""),
          title: String(r.title ?? ""),
          description: r.description ?? null,
          items: Array.isArray(r.items)
            ? r.items.map((it: any) => ({
                product_id: String(it.product_id),
                qty: Math.max(1, Number(it.qty) || 1),
              }))
            : [],
          discount_pct: r.discount_pct == null ? null : Number(r.discount_pct),
        }))
      );
    };
    fetchBundles();

    const channel = supabase
      .channel("ai_bundles-public")
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "ai_bundles" },
        () => { fetchBundles(); }
      )
      .subscribe();

    return () => {
      cancelled = true;
      supabase.removeChannel(channel);
    };
  }, [limit]);

  return { bundles, loading };
}